import React, { useMemo, useState } from "react";
import { Input } from "antd";
import { EyeFilled, SearchOutlined, SwapOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { StatusTag } from "../components/StatusTag";
import { useTailSpend } from "../TailSpendContext";
import { dealStageLabel } from "../model/api";
import { isTerminal, stageKindLabel } from "../model/lifecycle";
import { Deal, Supplier, SupplierId, Tone } from "../model/types";
import classes from "./SuppliersView.module.less";
import shared from "./shared.module.less";

const supplierPath = (id: string) =>
    `/superadmin/demo/tail-spend/suppliers/${id}`;
const dealPath = (id: string) => `/superadmin/demo/tail-spend/negotiations/${id}`;

// Parse "In N days" → N for sorting; suppliers without a near renewal sort
// last in a stable bucket.
const FAR_FUTURE = 9_999;
function renewalSortKey(s: Supplier): number {
    const w = s.nextRenewal?.window;
    if (!w) return FAR_FUTURE;
    const m = w.match(/(\d+)\s*days?/i);
    if (m) return parseInt(m[1], 10);
    return FAR_FUTURE - 1; // has a renewal but no day count → just before "no renewal"
}

function supplierStatus(s: Supplier): { label: string; tone: Tone } {
    if (s.watch?.kind === "creeping_auto_renewal")
        return { label: "Auto-renew flagged", tone: "amber" };
    if (s.watch?.kind === "expiring") return { label: "Expiring", tone: "amber" };
    if (s.watch?.kind === "price_drift") return { label: "Price drift", tone: "amber" };
    if (s.watch?.kind === "off_contract") return { label: "Monitoring", tone: "slate" };
    if (s.currentDealId) return { label: "Active", tone: "green" };
    if (!s.hasLiveAgreement) return { label: "Monitoring", tone: "slate" };
    return { label: "Active", tone: "green" };
}

export const SuppliersView: React.FC = () => {
    const { supplierId } = useParams();
    const { workspace } = useTailSpend();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matched = workspace.suppliers.filter((s) => {
            if (!q) return true;
            const cat = workspace.categories.find((c) => c.id === s.categoryId);
            return (
                s.name.toLowerCase().includes(q) ||
                (cat?.name.toLowerCase().includes(q) ?? false)
            );
        });
        // Default sort: nearest renewal first.
        return [...matched].sort(
            (a, b) => renewalSortKey(a) - renewalSortKey(b)
        );
    }, [workspace.suppliers, workspace.categories, search]);

    const dealsBySupplier = useMemo(() => {
        const map = new Map<SupplierId, Deal[]>();
        for (const d of workspace.deals) {
            const arr = map.get(d.supplierId) ?? [];
            arr.push(d);
            map.set(d.supplierId, arr);
        }
        return map;
    }, [workspace.deals]);

    const selectedId = supplierId as SupplierId | undefined;

    const selectSupplier = (id: SupplierId) => {
        if (selectedId === id) {
            navigate("/superadmin/demo/tail-spend/suppliers");
            return;
        }
        navigate(supplierPath(id));
    };

    return (
        <>
            <PageHeader
                title="Suppliers"
                subtitle="Your in-scope tail base. Each supplier carries its negotiation history, the terms on record, and what the agent is watching."
            />

            <div className={shared.toolbar}>
                <span />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter suppliers or categories…"
                    prefix={<SearchOutlined />}
                    className={shared.filterInput}
                    style={{ width: 320 }}
                />
            </div>

            <table className={`${shared.activeTable} ${classes.suppliersTable}`}>
                <thead>
                    <tr>
                        <th>Supplier</th>
                        <th>Category</th>
                        <th>Annual spend</th>
                        <th>Status</th>
                        <th>Terms on record</th>
                        <th>Negotiations</th>
                        <th>Negotiation status</th>
                        <th>Next renewal</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((s) => {
                        const cat = workspace.categories.find(
                            (c) => c.id === s.categoryId
                        );
                        const status = supplierStatus(s);
                        const supplierDeals = dealsBySupplier.get(s.id) ?? [];
                        const inFlight = supplierDeals.find(
                            (d) => !isTerminal(d.state)
                        );
                        const isSelected = selectedId === s.id;
                        return (
                            <React.Fragment key={s.id}>
                                <tr
                                    className={
                                        isSelected ? classes.rowSelected : undefined
                                    }
                                    onClick={() => selectSupplier(s.id)}
                                >
                                    <td>
                                        <div className={shared.supName}>{s.name}</div>
                                        <div className={shared.supEmail}>
                                            {s.primaryEmail}
                                        </div>
                                    </td>
                                    <td className={shared.muted}>{cat?.name}</td>
                                    <td className={shared.mono}>
                                        ${s.annualSpend.amount.toLocaleString()}
                                    </td>
                                    <td>
                                        <StatusTag tone={status.tone}>
                                            {status.label}
                                        </StatusTag>
                                    </td>
                                    <td className={classes.termsCell}>
                                        {s.termsSummary}
                                    </td>
                                    <td className={shared.muted}>
                                        {supplierDeals.length}
                                    </td>
                                    <td>
                                        {inFlight ? (
                                            <StatusTag tone={inFlight.statusChip.tone}>
                                                {inFlight.statusChip.label ||
                                                    stageKindLabel(inFlight.state)}
                                            </StatusTag>
                                        ) : (
                                            <span className={shared.muted}>—</span>
                                        )}
                                    </td>
                                    <td className={shared.muted}>
                                        {status.label === "Monitoring"
                                            ? "—"
                                            : s.nextRenewal?.window ?? "—"}
                                    </td>
                                </tr>
                                {isSelected && (
                                    <tr className={classes.expandRow}>
                                        <td colSpan={8}>
                                            <NegotiationsList
                                                deals={supplierDeals}
                                                onOpen={(id) =>
                                                    navigate(dealPath(id))
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};

const NegotiationsList: React.FC<{
    deals: Deal[];
    onOpen: (id: string) => void;
}> = ({ deals, onOpen }) => {
    if (deals.length === 0) {
        return (
            <div className={classes.empty}>
                <EyeFilled />
                <div>
                    <div className={classes.emptyTitle}>
                        No negotiations on record
                    </div>
                    <div className={classes.emptyDetail}>
                        This supplier is being monitored. The agent opens a
                        negotiation when a trigger fires.
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={classes.dealList}>
            {deals.map((d) => {
                const closedAt = d.statValuePairs.find(([k]) => k === "Closed")?.[1];
                return (
                    <button
                        key={d.id}
                        className={classes.dealRow}
                        onClick={() => onOpen(d.id)}
                    >
                        <div className={classes.dealIcon}>
                            <SwapOutlined />
                        </div>
                        <div className={classes.dealMain}>
                            <div className={classes.dealTitle}>
                                {stageKindLabel(d.state)}
                                {d.statusChip.label
                                    ? ` · ${d.statusChip.label}`
                                    : ""}
                            </div>
                            <div className={classes.dealSub}>
                                {dealStageLabel(d)} · {d.resultLabel}
                                {closedAt ? ` · ${closedAt}` : ""}
                            </div>
                        </div>
                        <StatusTag tone={d.statusChip.tone}>
                            {d.statusChip.label || stageKindLabel(d.state)}
                        </StatusTag>
                    </button>
                );
            })}
        </div>
    );
};
